import Styles from './PostApproval.module.scss'
import { Approval, XCircleContained } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useUpdateFile } from 'hooks/queries/files/useUpdateFile'
import { useQueryClient } from 'react-query'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { showToastMessage, ToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showToast } from 'common/ToastMessage'
import dayjs from 'dayjs'
import { useRef } from 'react'
import { message } from 'antd'

interface ReviewButtonsProps {
  almanacFile: AlmanacFileShowItem
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  onDelete: () => void
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
}

export const ReviewButtons = ({
  almanacFile,
  isLoading,
  setIsLoading,
  onDelete,
  onUpdateAlmanacFile,
}: ReviewButtonsProps) => {
  const queryClient = useQueryClient()
  const workspace = useCurrentWorkspace()
  const originalPostAt = useRef(almanacFile.post_at)

  const updateFileMutation = useUpdateFile(almanacFile.key, {
    onSuccess: (updatedFile) => {
      if (onUpdateAlmanacFile) {
        onUpdateAlmanacFile(updatedFile as AlmanacFileShowItem)
      }
      queryClient.invalidateQueries(filesKeys.partialCalendar(workspace?.id))
      queryClient.invalidateQueries(['workspaces', workspace?.id, 'schedules'])
    },
  })

  const handleUndoDontPost = async () => {
    setIsLoading(true)

    try {
      await updateFileMutation.mutateAsync({
        post_at: originalPostAt.current,
        post_status: PostStatusPropertyValue.Draft,
        reviewed_at: null,
      })
    } finally {
      message.destroy()
      setIsLoading(false)
    }
  }

  const UndoToastMessage = () => {
    return (
      <ToastMessage
        toastMessage="Post moved to drafts"
        buttonProps={{
          label: 'Undo',
          onPress: handleUndoDontPost,
          variant: 'cancel',
          type: 'button',
        }}
      />
    )
  }

  const handleApprove = async () => {
    setIsLoading(true)

    try {
      await updateFileMutation.mutateAsync({
        post_status: PostStatusPropertyValue.AutoPostScheduled,
        reviewed_at: dayjs().toISOString(),
      })
    } catch (error) {
      showToastMessage(`Failed to approve post`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDontPost = async () => {
    setIsLoading(true)

    try {
      // "Don't Post" means removing post_at and setting status to Draft
      await updateFileMutation.mutateAsync({
        post_at: null,
        post_status: PostStatusPropertyValue.Draft,
        reviewed_at: dayjs().toISOString(),
      })
      onDelete()
      showToast(<UndoToastMessage />, 3)
    } catch (error) {
      showToastMessage(`Failed to cancel post`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={Styles.buttons}>
      <Button
        variant="tertiary"
        size="md"
        frontIcon={XCircleContained}
        fullWidth
        onPress={handleDontPost}
        isDisabled={isLoading}
      >
        Don't Post
      </Button>
      <Button variant="green" size="md" frontIcon={Approval} fullWidth onPress={handleApprove} isDisabled={isLoading}>
        Approve
      </Button>
    </div>
  )
}
